/**
 * FooterBestSellers.jsx — Top 3 boutique picks on the site footer
 * Iter 391 · Feb 15 2026
 *
 * Renders best sellers from /api/store/best-sellers. Silently hides
 * itself when the store isn't ready (nothing published yet).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Leaf, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FooterBestSellers({ language = "en" }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/store/best-sellers`, { params: { limit: 3 } })
      .then((r) => setProducts(r.data?.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || products.length === 0) return null;

  const t = language === "fr"
    ? {
        eyebrow: "Boutique · Best-sellers",
        heading: "Ce que Karine recommande cette semaine",
        subline: "Produits biologiques Éminence — hand-picked, gagnez des points Crown Reserve à chaque achat.",
        cta: "Découvrir la boutique",
        priceCad: (n) => `${Number(n).toFixed(2)} $`,
        buy: "Voir",
      }
    : {
        eyebrow: "Boutique · Best-sellers",
        heading: "Karine's picks this week",
        subline: "Certified organic Eminence products — hand-picked by Karine. Earn Crown Reserve points on every purchase.",
        cta: "Explore the boutique",
        priceCad: (n) => `$${Number(n).toFixed(2)}`,
        buy: "View",
      };

  return (
    <div className="border-t border-white/10 py-12" data-testid="footer-best-sellers">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Leaf className="w-4 h-4 text-emerald-400" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">{t.eyebrow}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif text-white">{t.heading}</h2>
            <p className="text-sm text-white/60 mt-2 max-w-2xl">{t.subline}</p>
          </div>
          <Link
            to="/shop"
            className="hidden md:inline-flex items-center gap-2 text-emerald-300 hover:text-white text-sm whitespace-nowrap"
            data-testid="footer-best-sellers-cta"
          >
            {t.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {products.map((p) => (
            <Link
              key={p.sku}
              to={`/shop/${p.sku}`}
              className="group flex sm:flex-col gap-4 sm:gap-0 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl overflow-hidden transition"
              data-testid={`footer-best-seller-${p.sku}`}
            >
              <div className="w-24 h-24 sm:w-full sm:h-44 flex-shrink-0 bg-gradient-to-br from-emerald-100/10 to-teal-100/5 overflow-hidden">
                {p.image_url && (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <span className="text-[9px] uppercase tracking-widest text-emerald-300 mb-1">{p.category}</span>
                <h3 className="text-sm sm:text-base text-white font-medium line-clamp-2 mb-2">{p.name}</h3>
                <div className="mt-auto flex items-center justify-between">
                  <span className="text-sm text-white font-medium">{t.priceCad(p.price_cad)}</span>
                  <span className="text-xs text-emerald-300 group-hover:translate-x-1 transition-transform">
                    {t.buy} →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <Link
          to="/shop"
          className="md:hidden mt-4 flex items-center justify-center gap-2 py-3 rounded-full bg-white/5 hover:bg-white/10 text-emerald-200 text-sm"
        >
          {t.cta} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
